import { api } from "./api";

export interface ExamQuestion {
  id: string;
  questionText: string;
  options: Array<{ id: string; optionText: string }>;
  points: number;
  order?: number;
}

export interface CertificateExam {
  _id: string;
  title: string;
  description?: string;
  passingScore: number;
  timeLimit: number;
  examDuration: number;
  attemptsAllowed: number;
  questions: ExamQuestion[];
  totalPoints: number;
  showAnswersImmediately?: boolean;
}

export interface ExamResult {
  score: number;
  passed: boolean;
  attempts: number;
  correctAnswers?: number;
  totalQuestions?: number;
  answers?: Record<string, string>;
}

export const certificateExamApi = {
  // Get certificate exam for a course (admin/instructor)
  getCertificateExamByCourse: (courseId: string) => {
    return api.get<{ success: boolean; data: CertificateExam }>(
      `/certificate-exams/course/${courseId}`
    );
  },

  // Get certificate exam for student to take
  getCertificateExamForStudent: (courseId: string) => {
    return api.get<{ success: boolean; data: CertificateExam }>(
      `/certificate-exams/course/${courseId}/take`
    );
  },

  // Submit certificate exam
  submitCertificateExam: (examId: string, data: { answers: Record<string, string>; timeSpent?: number }) => {
    return api.post<{
      success: boolean;
      message: string;
      data: ExamResult;
    }>(`/certificate-exams/${examId}/submit`, data);
  },

  // Get exam result
  getCertificateExamResult: (examId: string) => {
    return api.get<{
      success: boolean;
      data: {
        exam: CertificateExam;
        result: ExamResult;
      };
    }>(`/certificate-exams/${examId}/result`);
  },

  // Create certificate exam (admin/instructor)
  createCertificateExam: (data: {
    courseId: string;
    title?: string;
    description?: string;
    passingScore?: number;
    timeLimit?: number;
    examDuration?: number;
    attemptsAllowed?: number;
    randomizeQuestions?: boolean;
    showAnswersImmediately?: boolean;
    requireProctoring?: boolean;
    questions?: ExamQuestion[];
    isActive?: boolean;
  }) => {
    return api.post<{ success: boolean; data: CertificateExam }>(
      "/certificate-exams",
      data
    );
  },

  // Update certificate exam (admin/instructor)
  updateCertificateExam: (examId: string, data: Partial<CertificateExam>) => {
    return api.put<{ success: boolean; data: CertificateExam }>(
      `/certificate-exams/${examId}`,
      data
    );
  },

  // Delete certificate exam (admin/instructor)
  deleteCertificateExam: (examId: string) => {
    return api.delete<{ success: boolean; message: string }>(
      `/certificate-exams/${examId}`
    );
  },
};